import {
  TelemetryType,
  type PzRacingDataRow,
  type RawTelemetryData,
  type GpsReading,
  type Device,
  calculateDistance,
} from '@pitbrain/shared'
import useApi from '../../api/useApi'
import ConfigurationContext from '../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { useBaseProcessor } from './useBaseProcessor'

export const usePzRacingProcesser = () => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { loadTrack, getFirstCoordinate, processAndCreateSession } =
    useBaseProcessor()
  const { error: apiError } = api

  /**
   * Parse PzRacing data into raw telemetry format
   */
  const parsePzRacingToRawTelemetry = (
    results: PzRacingDataRow[]
  ): RawTelemetryData => {
    let totalDistance = 0
    const gps: GpsReading[] = results.map(
      (p: PzRacingDataRow, index, array) => {
        if (index > 0) {
          const prev = array[index - 1]
          totalDistance += calculateDistance(
            prev.latitude,
            prev.longitude,
            p.latitude,
            p.longitude
          )
        }

        return {
          latitude: p.latitude,
          longitude: p.longitude,
          altitude: undefined,
          speedMs: p.speed / 3.6, // Convert km/h to m/s
          timestamp: new Date(p.date),
          distanceMeters: totalDistance,
        }
      }
    )

    return {
      gps,
      gyroscope: undefined,
      accelerometer: undefined,
      recordingStartTime: gps[0]?.timestamp,
      device: {
        deviceName: 'PzRacing',
        deviceVersion: '1.0',
      },
    }
  }

  const processFile = async (file: File, sessionId: string): Promise<void> => {
    const storedTrackSession = await api.trackSessionApi.getTrackSessionByName(
      file.name,
      [TelemetryType.Gps]
    )
    if (storedTrackSession) {
      const sessionFeatures = { hasVideo: false, hasLeanAngle: false }
      await loadTrack(storedTrackSession, sessionFeatures)
      return
    }

    const results = await api.fileApi.convertSesFile(file)
    const rawTelemetry = parsePzRacingToRawTelemetry(results)
    const firstCoordinate = getFirstCoordinate(rawTelemetry)
    if (firstCoordinate == null) return

    const track = await api.trackApi.findTrack(firstCoordinate)
    const device: Device = { name: 'PzRacing', version: '1.0' }

    await processAndCreateSession(
      rawTelemetry,
      sessionId,
      track,
      { hasLeanAngle: false, hasVideo: false },
      file.name,
      device
    )
  }

  return { processFile, error: apiError }
}
